import axios from 'axios';
import { API_BASE_URL } from '../config/api';

if (process.env.NODE_ENV === 'development') {
  console.log('EZaccounting API URL:', API_BASE_URL);
}


export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the browser set multipart boundary — a manual Content-Type breaks uploads.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Prevent redirect loops
      const isLoggingOut = localStorage.getItem('isLoggingOut') === 'true';
      if (!isLoggingOut) {
        localStorage.setItem('isLoggingOut', 'true');
        localStorage.removeItem('token');
        localStorage.removeItem('businessId');
        // Soft-redirect to login without forcing a full reload
        if (typeof window !== 'undefined' && window.history) {
          window.history.replaceState(null, '', '/login');
        } else {
          window.location.href = '/login';
        }
        // Clear the flag after a short delay
        setTimeout(() => {
          localStorage.removeItem('isLoggingOut');
        }, 2000);
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (userData: any) =>
    api.post('/auth/register', userData),
  getMe: () =>
    api.get('/auth/me'),
  updateProfile: (data: any) =>
    api.put('/auth/profile', data),
  changePassword: (data: any) =>
    api.put('/auth/change-password', data),
  // Public endpoints for registration
  getCountries: () =>
    api.get('/auth/countries'),
  getCurrencies: () =>
    api.get('/auth/currencies'),
};

export const accountingAPI = {
  // Chart of Accounts
  getAccounts: (filters?: any) =>
    api.get('/accounting/accounts', { params: filters }),
  getAccountHierarchy: () =>
    api.get('/accounting/accounts/hierarchy'),
  getAccount: (id: number) =>
    api.get(`/accounting/accounts/${id}`),
  createAccount: (data: any) =>
    api.post('/accounting/accounts', data),
  updateAccount: (id: number, data: any) =>
    api.put(`/accounting/accounts/${id}`, data),
  deleteAccount: (id: number) =>
    api.delete(`/accounting/accounts/${id}`),
  bulkDeleteAccounts: (ids: number[]) =>
    api.delete('/accounting/accounts/bulk', { data: { ids } }),
  initializeAccounts: () =>
    api.post('/accounting/accounts/initialize'),

  // Journal Entries
  getJournalEntries: (filters?: any) =>
    api.get('/accounting/journal-entries', { params: filters }),
  getJournalEntry: (id: number) =>
    api.get(`/accounting/journal-entries/${id}`),
  createJournalEntry: (data: any) =>
    api.post('/accounting/journal-entries', data),
  createCompoundEntry: (data: any) =>
    api.post('/accounting/journal-entries/compound', data),
  updateJournalEntry: (id: number, data: any) =>
    api.put(`/accounting/journal-entries/${id}`, data),
  deleteJournalEntry: (id: number) =>
    api.delete(`/accounting/journal-entries/${id}`),

  // Reports
  getTrialBalance: (filters?: any) =>
    api.get('/accounting/trial-balance', { params: filters }),
  getAccountEntries: (id: number, filters?: any) =>
    api.get(`/accounting/accounts/${id}/entries`, { params: filters }),
  validateBalance: (entries: any) =>
    api.post('/accounting/validate-balance', { entries }),
};

export const reportsAPI = {
  getMonthlyProfitLoss: (params?: { months?: number; business_id?: number }) =>
    api.get('/reports/profit-loss/monthly', { params }),
};

export const dashboardAPI = {
  getActivityChart: (params?: { months?: number; business_id?: number }) =>
    api.get('/dashboard/activity-chart', { params }),
};

export const voiceAPI = {
  // Legacy Voice Interface
  processCommand: (data: any) =>
    api.post('/voice/process', data),
  getSuggestions: () =>
    api.get('/voice/suggestions'),
  getHistory: (filters?: any) =>
    api.get('/voice/history', { params: filters }),
  startSession: (context?: any) =>
    api.post('/voice/session/start', { context }),
  endSession: (sessionId: string) =>
    api.post(`/voice/session/${sessionId}/end`),
  getSessionContext: (sessionId: string) =>
    api.get(`/voice/session/${sessionId}/context`),
  updateSessionContext: (sessionId: string, context: any) =>
    api.put(`/voice/session/${sessionId}/context`, { context }),
  processMultiStep: (data: any) =>
    api.post('/voice/multi-step', data),
  getCommands: () =>
    api.get('/voice/commands'),
  testCommand: (command: string, dryRun?: boolean) =>
    api.post('/voice/test', { command, dryRun }),
  
  // Enhanced Voice Interface
  processEnhanced: (data: any) =>
    api.post('/voice/process', {
      voice_text: data.text,
      current_page: data.context?.current_page || window.location.pathname
    }),
  processEnhancedAudio: (formData: FormData) =>
    api.post('/voice/process-enhanced', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  recognize: (formData: FormData) =>
    api.post('/voice/recognize', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  detectIntent: (data: any) =>
    api.post('/voice/detect-intent', data),
  execute: (data: any) =>
    api.post('/voice/execute', data),
  synthesize: (data: any) =>
    api.post('/voice/synthesize', data),
  synthesizeMultilingual: (data: any) =>
    api.post('/voice/synthesize-multilingual', data),
  getCapabilities: () =>
    api.get('/voice/capabilities'),
  // Test ElevenLabs voice (JSON response with audio_url). api_key optional when saved server-side.
  testElevenLabsVoice: (data: { api_key?: string; voice_id: string; text: string; language?: string }) =>
    api.post('/voice/elevenlabs/test', data),
  generateSpeech: (data: { text: string; voice_id?: string; language?: string }) =>
    api.post('/voice/speech', data),
  
  // Multilingual Voice Interface
  processMultilingual: (data: any) =>
    api.post('/voice/process-multilingual', data),
  processMultilingualAudio: (formData: FormData) =>
    api.post('/voice/process-multilingual', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  processLanguageSpecific: (language: string, data: any) =>
    api.post(`/voice/process/${language}`, data),
  processLanguageSpecificAudio: (language: string, formData: FormData) =>
    api.post(`/voice/process/${language}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getLanguageCommands: (language: string) =>
    api.get(`/voice/commands/${language}`),
  detectLanguage: (data: any) =>
    api.post('/voice/detect-language', data),
  getSupportedLanguages: () =>
    api.get('/voice/languages'),
};

export const imageAPI = {
  processImage: (formData: FormData) =>
    api.post('/image/process', formData),
  extractText: (formData: FormData) =>
    api.post('/image/ocr', formData),
  processReceipt: (formData: FormData) =>
    api.post('/image/receipt', formData),
  processInvoice: (formData: FormData) =>
    api.post('/image/invoice', formData),
  reviewExtraction: (processingId: string, data: any) =>
    api.put(`/image/review/${processingId}`, data),
  getHistory: (filters?: any) =>
    api.get('/image/history', { params: filters }),
  getProcessingDetails: (id: string) =>
    api.get(`/image/processing/${id}`),
  retryProcessing: (processingId: string) =>
    api.post(`/image/retry/${processingId}`),
  deleteProcessing: (id: string) =>
    api.delete(`/image/processing/${id}`),
  getFormats: () =>
    api.get(`/image/formats?t=${Date.now()}`),
  validateImage: (formData: FormData) =>
    api.post('/image/validate', formData),
  batchProcess: (formData: FormData) =>
    api.post('/image/batch', formData),
};


export const businessAPI = {
  // Business Management
  getAll: () =>
    api.get('/business'),
  getById: (id: number) =>
    api.get(`/business/${id}`),
  create: (data: any) =>
    api.post('/business', data),
  update: (id: number, data: any) =>
    api.put(`/business/${id}`, data),
  delete: (id: number) =>
    api.delete(`/business/${id}`),
  getSummary: (id: number) =>
    api.get(`/business/${id}/summary`),
  getUserBusinesses: () =>
    api.get('/business/user-businesses'),

  // Business Users
  getUsers: (id: number) =>
    api.get(`/business/${id}/users`),
  addUser: (id: number, data: any) =>
    api.post(`/business/${id}/users`, data),
  removeUser: (id: number, userId: number) =>
    api.delete(`/business/${id}/users/${userId}`),

  // Business Modules
  getModules: (id: number) =>
    api.get(`/business/${id}/modules`),
  enableModule: (id: number, moduleName: string) =>
    api.post(`/business/${id}/modules`, { module_name: moduleName }),
  disableModule: (id: number, moduleName: string) =>
    api.delete(`/business/${id}/modules/${moduleName}`),

  // Countries and Currencies
  getCountries: () =>
    api.get('/business/countries'),
  getCurrencies: () =>
    api.get('/business/currencies'),
  initialize: () =>
    api.post('/business/initialize'),
};

export const contactAPI = {
  // Contact Management
  getAll: (businessId: number, filters?: any) =>
    api.get('/contacts', { params: { business_id: businessId, ...filters } }),
  getCustomers: (businessId: number, filters?: any) =>
    api.get('/contacts', { params: { business_id: businessId, contact_type: 'customer', ...filters } }),
  getSuppliers: (businessId: number, filters?: any) =>
    api.get('/contacts', { params: { business_id: businessId, contact_type: 'supplier', ...filters } }),
  getById: (id: number) =>
    api.get(`/contacts/${id}`),
  create: (data: any) =>
    api.post('/contacts', data),
  update: (id: number, data: any) =>
    api.put(`/contacts/${id}`, data),
  delete: (id: number) =>
    api.delete(`/contacts/${id}`),
  getStats: (businessId: number) =>
    api.get(`/contacts/stats/${businessId}`),

  // Contact Transactions
  getTransactions: (id: number, limit?: number, offset?: number) =>
    api.get(`/contacts/${id}/transactions`, { params: { limit, offset } }),
  updateBalance: (id: number, amount: number, transactionType: string) =>
    api.put(`/contacts/${id}/balance`, { amount, transaction_type: transactionType }),

  // Bulk Operations
  import: (businessId: number, contacts: any[]) =>
    api.post('/contacts/import', { business_id: businessId, contacts }),
};

export const searchAPI = {
  search: (query: string, options?: { types?: string[]; limit?: number }) =>
    api.get('/search', { params: { q: query, ...options } }),
  getTypes: () => api.get('/search/types'),
};

export const settingsAPI = {
  // Voice Settings
  getVoiceSettings: () =>
    api.get('/settings/voice'),
  updateVoiceSettings: (data: any) =>
    api.put('/settings/voice', data),
  uploadCustomVoice: (formData: FormData) =>
    api.post('/settings/voice/upload-sample', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  // AI Integrations
  getIntegrations: () =>
    api.get('/settings/integrations'),
  updateIntegrations: (data: any) =>
    api.put('/settings/integrations', data),
  testIntegrations: (integrations: any) =>
    api.post('/settings/integrations/test', { integrations }),
  
  // Currencies
  getCurrencies: () =>
    api.get('/settings/currencies'),
};

export const integrationAPI = {
  // Generic ERP integration (all services)
  getServices: () =>
    api.get('/integration/services'),
  getConnections: () =>
    api.get('/integration/connections'),
  getAuthUrl: (service: string) =>
    api.get(`/integration/${service}/auth-url`),
  handleCallback: (service: string, code: string, state: string) =>
    api.post(`/integration/${service}/callback`, { code, state }),
  connectService: (service: string, credentials: Record<string, string>) =>
    api.post(`/integration/${service}/connect`, credentials),
  syncService: (service: string, data: { direction?: string; data_type?: string }) =>
    api.post(`/integration/${service}/sync`, data),
  getServiceStatus: (service: string) =>
    api.get(`/integration/${service}/status`),
  disconnectService: (service: string) =>
    api.delete(`/integration/${service}/disconnect`),
  getSyncHistory: (filters?: any) =>
    api.get('/integration/sync-history', { params: filters }),
  testConnection: (service: string) =>
    api.post(`/integration/test/${service}`),
  getMapping: (service: string) =>
    api.get(`/integration/mapping/${service}`),
  updateMapping: (service: string, mapping: any) =>
    api.put(`/integration/mapping/${service}`, { mapping }),
  exportData: (service: string, data: any) =>
    api.post(`/integration/export/${service}`, data),
  importData: (service: string, data: any) =>
    api.post(`/integration/import/${service}`, data),

  // Legacy aliases (QuickBooks, Zoho, Xero)
  getQuickBooksAuthUrl: () =>
    api.get('/integration/quickbooks/auth-url'),
  handleQuickBooksCallback: (code: string, state: string) =>
    api.post('/integration/quickbooks/callback', { code, state }),
  syncQuickBooks: (data: any) =>
    api.post('/integration/quickbooks/sync', data),
  getQuickBooksStatus: () =>
    api.get('/integration/quickbooks/status'),
  disconnectQuickBooks: () =>
    api.delete('/integration/quickbooks/disconnect'),

  getZohoAuthUrl: () =>
    api.get('/integration/zoho/auth-url'),
  handleZohoCallback: (code: string, state: string) =>
    api.post('/integration/zoho/callback', { code, state }),
  syncZoho: (data: any) =>
    api.post('/integration/zoho/sync', data),
  getZohoStatus: () =>
    api.get('/integration/zoho/status'),
  disconnectZoho: () =>
    api.delete('/integration/zoho/disconnect'),

  getXeroAuthUrl: () =>
    api.get('/integration/xero/auth-url'),
  handleXeroCallback: (code: string, state: string) =>
    api.post('/integration/xero/callback', { code, state }),
  syncXero: (data: any) =>
    api.post('/integration/xero/sync', data),
  getXeroStatus: () =>
    api.get('/integration/xero/status'),
  disconnectXero: () =>
    api.delete('/integration/xero/disconnect'),
};

export const conversationAPI = {
  // Process multimodal conversation input
  processMultimodal: (data: any) =>
    api.post('/conversation/process', data),
  
  // Process voice input with conversation context
  processVoice: (data: any) =>
    api.post('/conversation/voice', data),
  
  // Process text input with conversation context
  processText: (data: any) =>
    api.post('/conversation/text', data),
  
  // Process image input with conversation context
  processImage: (data: any) =>
    api.post('/conversation/image', data),
  
  // Process multimodal input (voice + image)
  processMultimodalInput: (data: any) =>
    api.post('/conversation/multimodal', data),
  
  // Get conversation context
  getContext: (sessionId: string) =>
    api.get(`/conversation/context/${sessionId}`),
  
  // Clear conversation context
  clearContext: (sessionId: string) =>
    api.delete(`/conversation/context/${sessionId}`),
  
  // Get active conversations
  getActiveConversations: () =>
    api.get('/conversation/active'),
};

// Quotes/Estimates API
export const quotesAPI = {
  list: (businessId: number, filters?: any) =>
    api.get('/quotes', { params: { business_id: businessId, ...filters } }),
  getById: (id: number) => api.get(`/quotes/${id}`),
  create: (data: any) => api.post('/quotes', data),
  update: (id: number, data: any) => api.put(`/quotes/${id}`, data),
  remove: (id: number) => api.delete(`/quotes/${id}`),
  accept: (id: number) => api.post(`/quotes/${id}/accept`),
  reject: (id: number) => api.post(`/quotes/${id}/reject`),
  approveToInvoice: (id: number) => api.post(`/quotes/${id}/approve-to-invoice`),
  bulkApproveToInvoices: (ids: number[]) => api.post('/quotes/bulk/approve-to-invoices', { ids }),
  bulkDelete: (quoteIds: number[]) => api.delete('/quotes/bulk/delete', { 
    data: { quote_ids: quoteIds },
    headers: { 'Content-Type': 'application/json' }
  })
};

export const paymentsAPI = {
  list: (filters?: any) => api.get('/payments', { params: filters }),
  getById: (id: number) => api.get(`/payments/${id}`),
  update: (id: number, data: any) => api.put(`/payments/${id}`, data),
  delete: (id: number) => api.delete(`/payments/${id}`),
  bulkDelete: (paymentIds: number[]) => api.delete('/payments/bulk/delete', {
    data: { payment_ids: paymentIds },
    headers: { 'Content-Type': 'application/json' }
  })
};
